import isFunction from 'lodash/isFunction';
import React, { useCallback, useRef, useState } from 'react';
import Cropper from 'react-cropper';
import { useTranslation } from 'react-i18next';

import { CropImageModalHeaderIcon } from 'src/components/Icons/Modals/CropImage/CropImageIcon';
import { CropImageModalRotateIcon } from 'src/components/Icons/Modals/CropImage/RotateIcon';
import { CropImageModalZoomIcon } from 'src/components/Icons/Modals/CropImage/ZoomIcon';
import { UiModal } from 'src/components/UI/modal/UiModal';
import { UiToast } from 'src/components/UI/toasts';
import { S3Upload, S3UploadReturnType } from 'src/utils/aws-s3';
import { readFileString } from 'src/utils/cropImageUtils';
import useScriptInvoked from 'src/utils/hooks/useScriptInvoked';
import { useTHelper } from 'src/utils/i18n';
import { SafeAnyType } from 'src/utils/safeAny';
import { StateFormOnChange } from 'src/utils/stateForm';
import { ChildrenPropType } from 'src/utils/types';

type Props = {
  formOnChange: StateFormOnChange;
  name: string;
  suggestedWidth: number;
  suggestedHeight: number;
  render: (childrenProps: { onStartChoosingFile: () => void; uploading: boolean }) => ChildrenPropType;

  onChange?: (data: S3UploadReturnType) => void;
  noCropper?: boolean;
  disabled?: boolean;
  fileNamePrefix?: string;
  multiple?: boolean; // allowed only for noCropper=true (without cropping modal)
  formats?: string;
};

const zoomInitialValue = 0;
const zoomMaxSteps = 7;
const zoomStepValue = 0.2;

export const UiCleanBaseImageUploader: React.FC<Props> = ({
  formOnChange,
  name,
  render,
  suggestedWidth,
  suggestedHeight,
  onChange,
  noCropper,
  disabled = false,
  fileNamePrefix,
  multiple,
  formats = 'image/*',
}: Props) => {
  const { t } = useTranslation();

  const tHelper = useTHelper('cropImage');

  /** moved to cdn because of having 2mb of size in the build bundle */
  useScriptInvoked('https://sdk.amazonaws.com/js/aws-sdk-2.912.0.js');

  const inputFileRef = useRef<HTMLInputElement>(null);
  const zoomSizeRef = useRef<number>(0);
  const zoomValueRef = useRef<number>(0);
  const cropperRef = useRef<SafeAnyType>(null);
  const fileName = useRef(['']);

  /** check if saving an image is cancelled */
  const cancelled = useRef(false);

  const [showModal, setShowModal] = useState(false);
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [, setRotation] = useState(0);
  const [zoom, setZoom] = useState<number>(zoomInitialValue);
  const [uploading, setUploading] = useState(false);

  const onCloseModal = useCallback(() => {
    setShowModal(false);
    setImageSrc(null);
    setRotation(0);
    setZoom(zoomInitialValue);

    fileName.current = [''];
  }, []);

  const uploadImage = useCallback(
    (files: (File | Blob)[]) => {
      Promise.allSettled(
        files.map((fileBody, index) => S3Upload(fileBody, { fileNamePrefix, fileName: fileName.current[index] }))
      )
        .then((result) => {
          if (!cancelled.current) {
            result.forEach((data) => {
              if (data.status !== 'rejected') {
                // pass initChange to display the error after submit
                formOnChange(name, data.value.fullPath, { trigger: false });

                if (isFunction(onChange)) {
                  onChange(data.value);
                }
              }
            });

            onCloseModal();
          }
        })
        .catch((e) => {
          if (!cancelled.current) {
            UiToast.error(tHelper('uploadError', { error: e.toString() }));
          }
        })
        .finally(() => {
          setUploading(false);
        });
    },
    [fileNamePrefix, formOnChange, name, onChange, onCloseModal, fileName, tHelper]
  );

  const onInputFileChange = useCallback(
    ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (files && !disabled) {
        const limitMB = 20;

        if (multiple) {
          setUploading(true);
          let overSize = false;

          const arrFiles = Array.from(files).filter(({ size }) => {
            const condition = size <= limitMB * 1024 * 1024;
            if (!condition) {
              overSize = true;
            }
            return condition;
          });

          if (overSize) {
            UiToast.error(
              t('common.someFileSizeTooBig', {
                limit: limitMB,
              })
            );
          }

          fileName.current = arrFiles.map(({ name }) => name);

          uploadImage(arrFiles);
        } else if (files[0].size > limitMB * 1024 * 1024) {
          UiToast.error(
            t('common.fileSizeTooBig', {
              limit: limitMB,
            })
          );
        } else if (noCropper) {
          fileName.current = [files[0].name];

          setUploading(true);
          uploadImage([files[0]]);
        } else {
          const givenFileName = files[0].name;

          readFileString(files[0])
            .then((file) => {
              fileName.current = [givenFileName];

              cancelled.current = false;

              setImageSrc(file);
              setShowModal(true);
            })
            .catch(() => {
              UiToast.error(t('common.errorReadFile'));
            });
        }

        // clear selected value
        (inputFileRef.current as SafeAnyType).value = '';
      }
    },
    [disabled, multiple, noCropper, t, uploadImage]
  );

  const onZoom = useCallback((e: SafeAnyType) => {
    const oldValue = zoomSizeRef.current;
    const currentValue = e.detail.ratio;

    if ((zoomValueRef.current >= zoomMaxSteps && currentValue > oldValue) || oldValue === currentValue) {
      e.preventDefault();
      return;
    }

    setZoom((v) => {
      zoomSizeRef.current = e.detail.ratio;

      // eslint-disable-next-line no-nested-ternary
      return oldValue > currentValue ? (v - 1 < zoomInitialValue ? zoomInitialValue : v - 1) : v + 1;
    });
  }, []);

  const onCancelUpload = useCallback(() => {
    cancelled.current = true;
    setUploading(false);
  }, []);

  const onSuccessUpload = useCallback(() => {
    setUploading(true);

    cropperRef.current.cropper
      .getCroppedCanvas({
        maxWidth: suggestedWidth,
      })
      .toBlob((blob: Blob | null) => {
        if (blob) {
          uploadImage([blob]);
        } else {
          UiToast.error(tHelper('errorGettingCroppedImage'));
          setUploading(false);
        }
      });
  }, [suggestedWidth, uploadImage, tHelper]);

  /** initiate choosing a file */
  const onStartChoosingFile = useCallback(() => {
    if (!disabled) {
      inputFileRef.current?.click();
    }
  }, [disabled]);

  return (
    <>
      <input
        type="file"
        accept={formats}
        ref={inputFileRef}
        className="d-none"
        onChange={onInputFileChange}
        multiple={multiple}
      />

      {render({
        onStartChoosingFile,
        uploading,
      })}

      {!noCropper && showModal && (
        <UiModal
          header={
            <>
              <CropImageModalHeaderIcon />
              {tHelper('title')}
            </>
          }
          modalClassName="crop-image-modal"
          cancelBtnText={tHelper('cancel')}
          successBtnText={tHelper('successBtnText')}
          successLoading={uploading}
          onCancel={onCancelUpload}
          onSuccess={onSuccessUpload}
          onHide={onCloseModal}
        >
          <>
            <div className="crop-image-main">
              <Cropper
                src={imageSrc || ''}
                style={{ height: 422, width: '100%' }}
                aspectRatio={suggestedWidth / suggestedHeight}
                viewMode={1}
                ref={cropperRef}
                dragMode="move"
                cropBoxResizable={false}
                cropBoxMovable={false}
                autoCropArea={1}
                wheelZoomRatio={zoomStepValue}
                zoom={onZoom}
              />
            </div>
            <div className="crop-image-btns">
              <div className="rotate">
                <CropImageModalRotateIcon
                  className="icon left"
                  onClick={() => {
                    setRotation((cur) => {
                      const val = cur - 90;
                      cropperRef.current?.cropper.rotateTo(val);

                      return val;
                    });

                    setTimeout(() => {
                      cropperRef.current?.cropper.zoom(-10);
                    }, 0);
                  }}
                />
                <CropImageModalRotateIcon
                  className="icon right"
                  onClick={() => {
                    setRotation((cur) => {
                      const val = cur + 90;
                      cropperRef.current?.cropper.rotateTo(val);

                      return val;
                    });

                    setTimeout(() => {
                      cropperRef.current?.cropper.zoom(-10);
                    }, 0);
                  }}
                />
              </div>

              <div className="border" />

              <div className="zoom">
                <CropImageModalZoomIcon className="icon small" />
                <input
                  type="range"
                  min={zoomInitialValue}
                  max={zoomInitialValue + zoomMaxSteps}
                  step="1"
                  value={zoom}
                  onChange={({ target: { value } }) => {
                    const newValue = +value;

                    // 3 times doing .zoom(0.1) is not equal to .zoom(0.3)
                    for (let i = 0; i < Math.abs(zoom - newValue); i += 1) {
                      cropperRef.current?.cropper.zoom(zoom > newValue ? -zoomStepValue : zoomStepValue);
                    }

                    setZoom(newValue);
                  }}
                />
                <CropImageModalZoomIcon className="icon" />
              </div>
            </div>
          </>
        </UiModal>
      )}
    </>
  );
};
